import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { parseCoils } from '../room-overview/utils';
import type { MRIMachinePopulated } from '../room-overview/types';

interface AddedScannersListProps {
    scanners: MRIMachinePopulated[];
    onRemove: (machineId: string) => void;
    showHeading?: boolean;
}

export default function AddedScannersList({
    scanners,
    onRemove,
    showHeading = true,
}: AddedScannersListProps) {
    const { t } = useTranslation();

    if (scanners.length === 0) return null;

    return (
        <div className="space-y-1.5">
            {showHeading && (
                <h3 className="text-sm font-medium text-muted-foreground">
                    {t('onboarding.addedScannersList.heading', { count: scanners.length })}
                </h3>
            )}
            {scanners.map((scanner) => {
                const coils = parseCoils(scanner.TRCoils);
                return (
                    <div
                        key={scanner._id}
                        className="flex items-center gap-3 rounded-lg border border-subtle bg-muted px-4 py-2.5"
                    >
                        <div className="min-w-0 flex-1 space-y-0.5">
                            <p className="text-sm font-medium text-foreground">
                                {scanner.Name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {scanner.Model} · {scanner.FieldStrength} T
                            </p>
                            {coils.length > 0 && (
                                <div className="flex flex-wrap gap-1 pt-0.5">
                                    {coils.map((coil, i) => (
                                        <Badge
                                            key={i}
                                            variant="outline"
                                            className="border-transparent bg-gray-200 text-xs text-gray-800"
                                        >
                                            {coil}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => onRemove(scanner._id)}
                            aria-label={t('onboarding.addedScannersList.removeScanner', { name: scanner.Name })}
                        >
                            <Trash2
                                className="h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                        </Button>
                    </div>
                );
            })}
        </div>
    );
}
